import { ReactNode, useMemo } from 'react';
import { useCurrentUser, useAllowedModules } from '../auth/AuthProvider';
import { resolveEffectiveModules } from '../config/modules';
import { Header, EmptyState } from '../components';

/**
 * Protege una ruta segun las pantallas efectivas del usuario (Rol + override
 * individual asignado en Configuracion > Usuarios). Si el usuario navega
 * directamente por URL a un modulo que no tiene permitido, ve un aviso claro
 * en vez de la pantalla (defensa en profundidad: el backend igual rechaza
 * las peticiones de esa pantalla con 403, ver requireModule en la API).
 */
export function RequireModule({ moduleId, children }: { moduleId: string; children: ReactNode }) {
  const user = useCurrentUser();
  const allowedModules = useAllowedModules();
  const effective = useMemo(() => resolveEffectiveModules(user.role, allowedModules), [user.role, allowedModules]);

  if (!effective.includes(moduleId)) {
    return (
      <div>
        <Header title="Acceso restringido" />
        <div className="p-4">
          <EmptyState icon="🔒" title="No tienes acceso a este módulo" description="Si necesitas usarlo, pide a un administrador que te lo habilite desde Configuración > Usuarios." />
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Protege una pagina EXCLUSIVA de Administrador (ej. Configuracion). A
 * diferencia de RequireModule, esto no depende de "Pantallas" ni tiene
 * override individual - Supervisor y Operador NUNCA pasan, sin excepcion.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const user = useCurrentUser();

  if (user.role !== 'admin') {
    return (
      <div>
        <Header title="Acceso restringido" />
        <div className="p-4">
          <EmptyState icon="🔒" title="Solo para administradores" description="Configuración administra usuarios, roles y actividades - es exclusiva del rol Administrador." />
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
